import { Loader } from "@/components";
import { Badge, Button } from "@/components/ui";
import { cn, stateLawStatus } from "@/lib/utils";
import { RootState } from "@/redux/store";
import { routes } from "@/routes/utils";
import { useDownloadPredefinedTemplate, useGetPredefinedTemplateList } from "@/services";
import { useQueryClient } from "@tanstack/react-query";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

const usePredefined = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [globalFilter, setGlobalFilter] = useState("");
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");

  const communityDetail = useSelector(
    (state: RootState) => state.currentData.communityData
  );
  
  const breadcrumbData = useMemo(
    () => [
      {
        link: `/${routes.admin}/${routes.masterData}/${routes.templates}`,
        name: `Templates - ${communityDetail?.name || ""}`,
      },
      {
        link: "",
        name: <span className="text-primary">Predefined Templates</span>,
      },
    ],
    []
  );
  
  const onBackClick = () => {
    navigate(`/${routes.admin}/${routes.templates}`);
  };

  const {data: predefinedTemplateList, isLoading: isTemplateLoading} = useGetPredefinedTemplateList()

  const {data: downloadTemplateData, isLoading: isTemplateDownloading} = useDownloadPredefinedTemplate({
    templateId: selectedTemplateId,
    queryParams: {
      enabled: !!selectedTemplateId
    }
  });

  useEffect(() => {
    if (downloadTemplateData) {
      const blob = new Blob([downloadTemplateData.data], {
        type: downloadTemplateData.headers["content-type"],
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      // Try to get filename from headers, fallback to fileName or default
      const contentDisposition = downloadTemplateData.headers["content-disposition"];
      let downloadFileName = "template.doc";
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match?.[1]) {
          downloadFileName = match[1];
        }
      }

      link.setAttribute("download", downloadFileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      queryClient.cancelQueries({queryKey: ["download-predefined-template", selectedTemplateId]})
      setSelectedTemplateId("");
    }
  }, [downloadTemplateData])

  // Define Table Columns
  const columns = [
    {
      accessorKey: "id",
      header: "ID",
      accessorFn: (row: any) => String(row.id || ""),
    },
    {
      accessorKey: "templateName",
      header: "Template Name",
      accessorFn: (row: any) => String(row.templateName || ""),
    },
    // {
    //   accessorKey: "versionNo",
    //   header: "Version No",
    //   accessorFn: (row: any) => String(row.versionNo || ""),
    // },
    {
      accessorKey: "createdAt",
      header: "Created On",
      accessorFn: (row: any) =>
        row.createdAt ? moment(row.createdAt).format("MM/DD/YYYY") : "",
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: (row: any) => {
        return (
          <Badge
            variant="outline"
            className={cn(
              (stateLawStatus as any)[row.row.original.status?.toLowerCase()]
                ?.color,
              "rounded-full px-4 py-2"
            )}
          >
            {
              (stateLawStatus as any)[row.row.original.status?.toLowerCase()]
                ?.label
            }
          </Badge>
        );
      },
    },
    {
      accessorKey: "download",
      header: "",
      cell: ({row}: any) => <Button variant="link" disabled={selectedTemplateId == row.original?.id && isTemplateDownloading} onClick={() => {
        setSelectedTemplateId(row.original?.id);
      }}>{'Download Template'}
        {selectedTemplateId == row.original?.id && isTemplateDownloading ? <Loader containerClassName="h-[20px]" className="h-5 w-5" /> : <></>}
      </Button>,
    },
  ];

  return {
    globalFilter,
    setGlobalFilter,
    breadcrumbData,
    onBackClick,
    predefinedTemplateList,
    isTemplateLoading,
    columns
  }
}

export default usePredefined;